/**
 * What happens to a coach's currency rows when they change Pricing Model.
 * Pure TypeScript - no React imports.
 *
 * | From → To         | Suggested / price | Minimum | Currency rows |
 * | ----------------- | ----------------- | ------- | ------------- |
 * | Free → Single Fee | enter             | —       | enter         |
 * | Free → PWYW       | enter             | enter   | one empty row |
 * | Single Fee → PWYW | carries over      | enter   | carry over    |
 * | PWYW → Single Fee | carries over      | dropped | carry over    |
 * | Single Fee → Free | zeroed            | —       | dropped       |
 * | PWYW → Free       | zeroed            | dropped | dropped       |
 *
 * Single Fee and Pay What You Want share the same underlying price field, which is why
 * amounts survive that switch rather than clearing.
 *
 * The table's "zeroed" column is the top-level scalar price field that live events and
 * booking services carry outside the currency array. That field is not part of a
 * currency row, so it is not this function's to zero — the forms that own it already do
 * so on the way to Free. What this function guarantees is the "Currency rows" column:
 * moving to Free drops the rows outright, so no row-held amount survives.
 */

import type {PricingCurrencyRow, PricingModel} from "./pricing.types";

/**
 * An unfilled row, ready for the coach to pick a currency and enter amounts.
 */
export function emptyPricingCurrencyRow(): PricingCurrencyRow {
  return {currency: "", minimum: undefined, suggested: undefined};
}

/**
 * Reshapes the currency rows for a change of Pricing Model.
 *
 * @param from - the model the coach is leaving
 * @param to - the model the coach has chosen
 * @param rows - the offering's current rows
 * @returns the rows the form should hold under the new model
 */
export function applyPricingModelTransition(
  from: PricingModel,
  to: PricingModel,
  rows: PricingCurrencyRow[]
): PricingCurrencyRow[] {
  if (from === to) {
    return rows;
  }

  // Nothing an offering that costs nothing could carry forward.
  if (to === "free") {
    return [];
  }

  // A Free offering has no pricing the coach chose, so nothing it holds is worth
  // carrying. Pay What You Want still needs a row to be completable; Single Fee is left
  // to the form's own empty state, exactly as it is today.
  if (from === "free") {
    return to === "pwyw" ? [emptyPricingCurrencyRow()] : [];
  }

  // Between Single Fee and Pay What You Want the amounts and currencies carry over —
  // only the Minimum Price changes hands, dropped one way and re-entered the other.
  return rows.map((row) => ({...row, minimum: undefined}));
}
